import type { GatewayBrowserClient } from "../gateway";

export type McpEnvField = {
  name: string;
  label: string;
};

export type McpCatalogEntry = {
  name: string;
  description: string;
  domain: string;
  package: string;
  requiredEnv: McpEnvField[];
};

export type McpServerEntry = {
  name: string;
  status: string;
  transport: string;
  tools: string[];
  toolCount: number;
  error?: string;
};

export type McpMessage = {
  kind: "success" | "error";
  message: string;
};

export type McpState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  mcpServers: McpServerEntry[];
  mcpCatalog: McpCatalogEntry[];
  mcpLoading: boolean;
  mcpError: string | null;
  mcpBusyServer: string | null;
  mcpMessages: Record<string, McpMessage>;
  /** Per-server env var inputs: { "notion": { "NOTION_TOKEN": "secret_..." } } */
  mcpEnvInputs: Record<string, Record<string, string>>;
};

function getErrorMessage(err: unknown) {
  if (err instanceof Error) return err.message;
  return String(err);
}

export async function loadMcpCatalog(state: McpState) {
  if (!state.client || !state.connected) return;
  try {
    const res = (await state.client.request("mcp.catalog", {})) as
      | { catalog: McpCatalogEntry[] }
      | undefined;
    if (res?.catalog) state.mcpCatalog = res.catalog;
  } catch (err) {
    state.mcpError = getErrorMessage(err);
  }
}

export async function loadMcpServers(state: McpState) {
  if (!state.client || !state.connected) return;
  if (state.mcpLoading) return;
  state.mcpLoading = true;
  state.mcpError = null;
  try {
    const res = (await state.client.request("mcp.list", {})) as
      | { servers: McpServerEntry[] }
      | undefined;
    if (res?.servers) state.mcpServers = res.servers;
  } catch (err) {
    state.mcpError = getErrorMessage(err);
  } finally {
    state.mcpLoading = false;
  }
}

export async function connectMcpServer(state: McpState, name: string) {
  if (!state.client || !state.connected) return;
  state.mcpBusyServer = name;
  state.mcpError = null;
  try {
    const env = state.mcpEnvInputs[name] ?? {};
    const result = (await state.client.request("mcp.connect", {
      name,
      env,
    })) as {
      ok?: boolean;
      name?: string;
      status?: string;
      tools?: string[];
      toolCount?: number;
      error?: string;
    };

    if (result?.ok) {
      state.mcpMessages[name] = {
        kind: "success",
        message: `Connected (${result.toolCount ?? 0} tools)`,
      };
    } else {
      state.mcpMessages[name] = {
        kind: "error",
        message: result?.error ?? "Connection failed",
      };
    }

    await loadMcpServers(state);
  } catch (err) {
    const message = getErrorMessage(err);
    state.mcpError = message;
    state.mcpMessages[name] = { kind: "error", message };
  } finally {
    state.mcpBusyServer = null;
  }
}

export async function disconnectMcpServer(state: McpState, name: string) {
  if (!state.client || !state.connected) return;
  state.mcpBusyServer = name;
  state.mcpError = null;
  try {
    await state.client.request("mcp.disconnect", { name });
    state.mcpMessages[name] = { kind: "success", message: "Disconnected" };
    await loadMcpServers(state);
  } catch (err) {
    const message = getErrorMessage(err);
    state.mcpError = message;
    state.mcpMessages[name] = { kind: "error", message };
  } finally {
    state.mcpBusyServer = null;
  }
}

export function updateMcpEnvInput(
  state: McpState,
  serverName: string,
  envName: string,
  value: string,
) {
  const current = state.mcpEnvInputs[serverName] ?? {};
  state.mcpEnvInputs = {
    ...state.mcpEnvInputs,
    [serverName]: { ...current, [envName]: value },
  };
}
